"use client";

import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import Link from "next/link";
import { useAuth } from "@/contexts/auth-context";
import { safeGet, safeSet } from "@/components/onboarding/safe-storage";
import { cn } from "@/lib/utils";

const STORAGE_PREFIX = "upsider_cancel_rescue_dismissed";
const RESCUE_WINDOW_DAYS = 7;

type RescueTarget = {
  subscriptionId: string;
  endsAtLabel: string;
};

function formatShortDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "soon";
  return date.toLocaleDateString(undefined, { month: "long", day: "numeric" });
}

export function CancelRescueBanner() {
  const { isLoading, user, subscriptions } = useAuth();
  const [shouldRender, setShouldRender] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [target, setTarget] = useState<RescueTarget | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const rafRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (isLoading || !user) {
      setIsOpen(false);
      setShouldRender(false);
      return;
    }

    const now = Date.now();
    const windowMs = RESCUE_WINDOW_DAYS * 24 * 60 * 60 * 1000;
    const candidate = (subscriptions ?? []).find((sub) => {
      if (sub.cancel_at_period_end !== true) return false;
      if (sub.status !== "active" && sub.status !== "trialing") return false;
      const endIso = (typeof sub.cancel_at === "string" && sub.cancel_at) || sub.period_end;
      if (!endIso) return false;
      const endMs = new Date(endIso).getTime();
      return Number.isFinite(endMs) && endMs > now && endMs - now <= windowMs;
    });

    if (!candidate) {
      setIsOpen(false);
      setShouldRender(false);
      return;
    }

    const storageKey = `${STORAGE_PREFIX}:${user.id}:${candidate.subscription_id}`;
    if (safeGet(storageKey) !== null) {
      setIsOpen(false);
      setShouldRender(false);
      return;
    }

    const endIso = (typeof candidate.cancel_at === "string" && candidate.cancel_at) || candidate.period_end;
    setTarget({
      subscriptionId: candidate.subscription_id,
      endsAtLabel: formatShortDate(endIso),
    });
    setShouldRender(true);
    rafRef.current = requestAnimationFrame(() => setIsOpen(true));

    return () => {
      if (rafRef.current !== undefined) cancelAnimationFrame(rafRef.current);
    };
  }, [isLoading, user, subscriptions]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
      if (rafRef.current !== undefined) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  function handleDismiss() {
    setIsOpen(false);
    if (user && target) {
      safeSet(`${STORAGE_PREFIX}:${user.id}:${target.subscriptionId}`, "1");
    }
    closeTimeoutRef.current = setTimeout(() => setShouldRender(false), 200);
  }

  if (!shouldRender || !target) return null;

  return (
    <section
      className={cn(
        "flex-none overflow-hidden",
        "transition-[max-height,opacity] duration-200 ease-out motion-reduce:transition-none",
        isOpen ? "max-h-14 opacity-100" : "max-h-0 opacity-0 pointer-events-none"
      )}
      aria-label="Subscription ending soon"
      aria-hidden={!isOpen}
    >
      <div className="relative border-b border-warning/20 bg-warning/[0.04]">
        <div className="absolute inset-y-0 left-0 w-[2px] bg-gradient-to-b from-warning/60 via-warning/30 to-transparent" />

        <div className="min-h-11 pl-4 pr-2 flex items-center gap-3 min-w-0">
          <p className="text-[13px] min-w-0 truncate">
            <span className="font-medium text-white/90">
              Your subscription ends {target.endsAtLabel}.
            </span>
            <span className="hidden sm:inline text-white/45"> Your setup stays saved either way.</span>
          </p>

          <div className="ml-auto flex items-center gap-2 shrink-0">
            <Link
              href={`/account?reactivate=${encodeURIComponent(target.subscriptionId)}`}
              className="inline-flex items-center rounded-md border border-warning/30 bg-warning/10 px-2.5 py-1 text-[12px] font-medium text-warning transition-colors hover:bg-warning/20"
            >
              Keep my subscription
            </Link>
            <div className="w-px h-4 bg-white/10" />
            <button
              type="button"
              onClick={handleDismiss}
              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded text-white/30 hover:text-white/60 hover:bg-white/5 transition-colors"
              aria-label="Dismiss"
            >
              <X className="size-3.5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
